import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError, admin, templates, type PreviewBody } from '../lib/api.js';
import type {
  AdminClientsResult,
  PermissionFlag,
  PreviewResult,
  Rule,
  RuleTarget,
} from '../lib/types.js';
import './DeleteRuleDialog.css';

const LABELS: Record<PermissionFlag, string> = {
  list: 'vedere',
  download: 'scaricare',
  upload: 'caricare',
  delete: 'cancellare',
  createFolder: 'creare cartelle',
  rename: 'rinominare',
};

/**
 * Conferma di eliminazione con l'impatto calcolato.
 *
 * Eliminare la regola base (`**`) toglie la navigazione a chi la subisce: il
 * portale diventa vuoto e l'errore che vede è un generico "elemento non
 * trovato". Va detto prima, non scoperto dopo — e vale a maggior ragione per
 * una regola personale, dove a restare fuori è una persona sola e nessuno se ne
 * accorge finché non telefona.
 */
export function DeleteRuleDialog({
  target,
  rule,
  onClose,
  onDeleted,
}: {
  target: RuleTarget;
  rule: Rule;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const orgs = useQuery<AdminClientsResult>({
    queryKey: ['admin', 'clients'],
    queryFn: () => admin.clients(),
    enabled: target.scope === 'template',
  });

  const candidate =
    target.scope === 'template'
      ? (orgs.data?.clients ?? []).filter((c) => c.isActive && c.templateId === target.templateId)
      : [];

  const impact = useMutation({
    mutationFn: (body: PreviewBody) => templates.preview(body),
  });

  // L'impatto si calcola appena si sa su cosa: l'admin non deve premere un
  // altro pulsante per sapere cosa sta per rompere.
  useEffect(() => {
    if (!impact.isIdle) return;
    const base = { rule: null, ruleId: rule.id, remove: true } as const;
    if (target.scope === 'template') {
      if (candidate.length === 0) return;
      impact.mutate({
        ...base,
        scope: 'template',
        templateId: target.templateId,
        clientIds: candidate.slice(0, 10).map((c) => c.id),
      });
    } else if (target.scope === 'client') {
      impact.mutate({ ...base, scope: 'client', clientId: target.clientId });
    } else {
      impact.mutate({
        ...base,
        scope: 'user',
        clientId: target.clientId,
        userId: target.userId,
      });
    }
  }, [candidate, impact, rule.id, target]);

  const remove = useMutation({
    mutationFn: () => templates.deleteRule(rule.id),
    onSuccess: onDeleted,
  });

  const preview = impact.data as PreviewResult | undefined;
  const lockout = preview?.lockout ?? [];
  const senzaBersaglio = target.scope === 'template' && candidate.length === 0;

  return (
    <div className="picker" role="dialog" aria-modal="true">
      <div className="picker__box card delrule">
        <header className="picker__head">
          <div>
            <h3>Eliminare questa regola?</h3>
            <p className="delrule__target">{target.label}</p>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <div className="delrule__body">
          <p className="delrule__rule">
            <code>{rule.pathPattern}</code>
          </p>

          {senzaBersaglio ? (
            <p className="delrule__note">
              Nessuna organizzazione attiva usa questo template: l'eliminazione non ha effetti
              immediati.
            </p>
          ) : impact.isPending ? (
            <p className="delrule__note">Calcolo dell'impatto…</p>
          ) : impact.isError ? (
            <div className="alert alert--error">
              Impossibile calcolare l'impatto. Procedi solo se sai cosa fa questa regola.
            </div>
          ) : preview ? (
            <>
              {lockout.length > 0 ? (
                <div className="delrule__lockout">
                  <strong>Attenzione: blocco totale dell'accesso.</strong>
                  <p>
                    Dopo l'eliminazione {lockout.join(', ')} non vedrebbe più{' '}
                    <strong>nulla</strong> nel portale: nessuna regola concederebbe la
                    navigazione della cartella principale.
                  </p>
                </div>
              ) : null}

              <Effetti preview={preview} />
            </>
          ) : null}
        </div>

        <footer className="picker__foot">
          <span className="delrule__foot">
            {lockout.length > 0
              ? 'Verifica di avere un\'altra regola che concede "vedere" sulla radice.'
              : 'L\'eliminazione è registrata nel log di audit.'}
          </span>
          <div className="delrule__actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Annulla
            </button>
            <button
              type="button"
              className="btn btn--danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Eliminazione…' : 'Elimina comunque'}
            </button>
          </div>
        </footer>

        {remove.error ? (
          <div className="alert alert--error delrule__error">
            {remove.error instanceof ApiError ? remove.error.message : 'Eliminazione non riuscita.'}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Effetti({ preview }: { preview: PreviewResult }) {
  const persi = (Object.keys(LABELS) as PermissionFlag[])
    .map((k) => ({ k, n: preview.totali[k]?.persi ?? 0 }))
    .filter((r) => r.n > 0);

  if (persi.length === 0) {
    return (
      <p className="delrule__note">
        Nessun permesso cambierebbe: questa regola è già coperta da un'altra più specifica, oppure
        non fa match su nulla.
      </p>
    );
  }

  return (
    <div className="delrule__effects">
      <p>Permessi che verrebbero revocati:</p>
      <ul>
        {persi.map((r) => (
          <li key={r.k}>
            <strong>{LABELS[r.k]}</strong> in {r.n} punt{r.n === 1 ? 'o' : 'i'}
          </li>
        ))}
      </ul>
      {preview.troncato ? (
        <p className="delrule__trunc">
          Analisi troncata al limite di {preview.limiti.maxNodi} nodi: i numeri sono parziali.
        </p>
      ) : null}
    </div>
  );
}
